import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';

// ── Tenant copy-rename (UI-only mini-item) ────────────────────────────────────
// Renames a tenant-owned template (an imported copy or a tenant-authored one) via the
// EXISTING PATCH /assessment-templates/:id { name } — tenant-authed, RLS-scoped to
// tenant_id IS NOT NULL (hub rows structurally excluded), no new endpoint/schema. Dual
// invalidation: the detail query + the list query (the row's name updates too). Server
// stays the boundary — its rejections surface to the caller (the modal).
export function usePatchTemplate(templateId: string) {
  const qc = useQueryClient();
  const { tenantId } = useAuthStore();
  return useMutation({
    mutationFn: async (body: { name?: string; pass_threshold_pct?: number }) => {
      const { data } = await apiClient.patch(`/assessment-templates/${templateId}`, body);
      return data.data as { id: string; name: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-template', templateId] });
      qc.invalidateQueries({ queryKey: ['assessment-templates', tenantId] });
    },
  });
}

export interface QuestionOp {
  op: 'add' | 'update' | 'delete' | 'reorder';
  id?: string;
  section_id?: string;
  question_text?: string;
  guidance_text?: string | null;
  display_order?: number;
  response_type?: string;
  max_score?: number;
  is_required?: boolean;
  ordered_ids?: string[];
}

// Partial patch for an existing question's editable fields (rides the QuestionOp 'update' op).
export interface QuestionUpdatePatch {
  question_text?: string;
  guidance_text?: string | null;
  max_score?: number;
  is_required?: boolean;
  response_type?: string;
}

export interface SectionOp {
  op: 'add' | 'update' | 'delete' | 'reorder';
  id?: string;
  name?: string;
  description?: string | null;
  display_order?: number;
  weight_pct?: number;
  ordered_ids?: string[];
}

export function usePatchQuestions(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (operations: QuestionOp[]) => {
      const { data } = await apiClient.patch(`/assessment-templates/${templateId}/questions`, { operations });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-template', templateId] });
    },
  });
}

export function usePatchSections(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (operations: SectionOp[]) => {
      const { data } = await apiClient.patch(`/assessment-templates/${templateId}/sections`, { operations });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-template', templateId] });
    },
  });
}

// ── VA.3.7: bypass config ─────────────────────────────────────────────────────

export function usePatchBypassConfig(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { allow_bypass: boolean }) => {
      const { data } = await apiClient.patch(`/assessment-templates/${templateId}/bypass-config`, dto);
      return data.data as { id: string; allow_bypass: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-template', templateId] });
    },
  });
}

export function usePatchQuestionBypassEligibility(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ questionId, certTypes }: { questionId: string; certTypes: string[] }) => {
      const { data } = await apiClient.patch(
        `/assessment-templates/${templateId}/questions/${questionId}/bypass-eligibility`,
        { cert_types: certTypes },
      );
      return data.data as { questionId: string; cert_types: string[] };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assessment-template', templateId] });
    },
  });
}
